import { useEffect, useState, useRef } from 'react';
import { X } from 'lucide-react';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

export function BottomSheet({ isOpen, onClose, title, children }: BottomSheetProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const touchStartY = useRef<number>(0);
  const sheetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setDragOffset(0);
      document.body.style.overflow = 'hidden';
    } else {
      const timer = setTimeout(() => setIsVisible(false), 300);
      document.body.style.overflow = '';
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Touch handlers for swipe-down-to-close (Apple behavior)
  const handleTouchStart = (e: React.TouchEvent) => {
    // Only allow dragging from the header/handle area
    const target = e.target as HTMLElement;
    const isHeaderOrHandle = target.closest('[data-drag-handle]');
    if (!isHeaderOrHandle) return;

    touchStartY.current = e.touches[0].clientY;
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;

    const currentY = e.touches[0].clientY;
    const deltaY = currentY - touchStartY.current;

    // Only allow dragging down (positive deltaY)
    if (deltaY > 0) {
      setDragOffset(deltaY);
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;

    setIsDragging(false);

    // If dragged down more than 100px, close the sheet
    if (dragOffset > 100) {
      onClose();
    }

    // Reset drag offset (with animation)
    setDragOffset(0);
  };

  if (!isVisible && !isOpen) return null;

  return (
    <div className={`fixed inset-0 z-50 flex flex-col justify-end transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0'}`}>
      {/* Enhanced Backdrop with stronger blur */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-[20px]"
        onClick={onClose}
        style={{ opacity: isDragging ? Math.max(0, 1 - dragOffset / 300) : undefined }}
      />

      {/* Liquid Glass Sheet */}
      <div
        ref={sheetRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className={`relative w-full max-h-[85vh] bg-[var(--tg-theme-bg-color)]/95 backdrop-blur-[40px] rounded-t-[28px] flex flex-col shadow-[0_-8px_48px_rgba(0,0,0,0.2)] border-t border-white/10 dark:border-white/8 ${isDragging ? '' : 'transition-transform duration-300'}`}
        style={{
          transform: isOpen
            ? `translateY(${dragOffset}px)`
            : 'translateY(100%)',
        }}
      >
        {/* Enhanced Handle with glow */}
        <div className="flex justify-center pt-4 pb-3" data-drag-handle>
          <div className="w-12 h-1.5 rounded-full bg-white/20 dark:bg-white/15 shadow-[0_0_8px_rgba(255,255,255,0.1)]" />
        </div>

        {/* Glassmorphic Header */}
        <div className="flex items-center justify-between px-6 pb-4 border-b border-white/8 dark:border-white/5" data-drag-handle>
          <h2 className="text-2xl font-black tracking-tight">{title}</h2>
          <button
            onClick={onClose}
            className="p-2.5 rounded-xl bg-[var(--tg-theme-secondary-bg-color)]/60 backdrop-blur-xl border border-white/10 dark:border-white/8 text-[var(--tg-theme-hint-color)] hover:text-[var(--tg-theme-text-color)] active:scale-95 transition-all duration-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content with custom scrollbar */}
        <div className="flex-1 overflow-y-auto px-6 py-6 pb-12 scrollbar-hide">
          {children}
        </div>
      </div>
    </div>
  );
}
